import { useState } from "react";
import axios from "axios";
import { ArrowRight, Gift, Heart, Users, CheckCircle2, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  parrain_nom: "",
  parrain_prenom: "",
  parrain_email: "",
  parrain_telephone: "",
  filleul_nom: "",
  filleul_prenom: "",
  filleul_email: "",
  filleul_telephone: "",
  filleul_ville: "",
  message: "",
  rgpd_consent: false,
};

const Parrainage = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd_consent) {
      setErrorMessage("Merci de cocher l'accord RGPD pour valider l'envoi.");
      return;
    }
    setStatus("sending");
    setErrorMessage("");
    try {
      const payload = { ...form };
      if (!payload.filleul_email) delete payload.filleul_email;
      await axios.post(`${API}/parrainage`, payload);
      setStatus("success");
      setForm(initialForm);
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err.response?.data?.detail ||
          "Une erreur est survenue. Merci de nous appeler directement au 02 55 99 66 25."
      );
    }
  };

  return (
    <div data-testid="page-parrainage">
      <SEO
        title="Parrainage propriétaires, 100 € offerts pour vous & votre filleul"
        description="Recommandez Terre et Mer Conciergerie à un voisin ou un ami propriétaire d'un bien Airbnb en Bretagne : 100 € de remise sur votre 2ᵉ mois pour vous, 100 € pour lui. Jusqu'à 10 parrainages par an."
        keywords="parrainage conciergerie airbnb, recommander terre et mer, 100 euros offerts, bonus propriétaire, bouche à oreille"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Parrainage", path: "/parrainage" },
        ]}
      />
      <PageHero
        overline="Parrainage propriétaires"
        title="Recommandez-nous,"
        italic="on vous remercie"
        description="Vous appréciez notre travail ? Recommandez Terre et Mer à un voisin ou ami propriétaire. 100 € de remise sur votre 2ᵉ mois de commission pour vous, 100 € pour lui."
        image="/images/parrainage-equipe-merci-confiance.jpg"
        cta={{ to: "#parrainage-form", label: "Parrainer maintenant" }}
      />

      {/* Bénéfices */}
      <section
        className="py-20 bg-gradient-to-br from-cream via-sand to-cream"
        data-testid="parrainage-benefits"
      >
        <div className="container-tm">
          <div className="text-center mb-14">
            <p className="overline mb-4">Comment ça marche</p>
            <h2 className="font-serif text-3xl md:text-5xl text-ink leading-tight max-w-3xl mx-auto">
              Une formule simple,{" "}
              <em className="text-gold not-italic">généreuse</em> et sans
              limite.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: <Users size={24} strokeWidth={1.6} />,
                title: "1. Vous recommandez",
                desc: "Vous remplissez notre formulaire avec les coordonnées de votre filleul (voisin, ami, famille, tout propriétaire d'un bien locatif sur notre zone).",
              },
              {
                icon: <Heart size={24} strokeWidth={1.6} />,
                title: "2. On le contacte",
                desc: "Notre équipe prend contact sous 48h pour une étude gratuite et personnalisée. Aucune pression, aucune obligation pour lui.",
              },
              {
                icon: <Gift size={24} strokeWidth={1.6} />,
                title: "3. On vous remercie",
                desc: "S'il signe avec nous, nous vous offrons 100 € de remise sur votre 2ᵉ mois de commission ET il bénéficie aussi de 100 € de remise sur son 2ᵉ mois.",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl p-8 shadow-soft border border-ink/5"
                data-testid={`parrainage-step-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gold/15 text-gold flex items-center justify-center mb-5">
                  {step.icon}
                </div>
                <h3 className="font-serif text-xl text-ink mb-3">
                  {step.title}
                </h3>
                <p className="text-ink/70 text-sm leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Cumul */}
          <div className="mt-12 max-w-3xl mx-auto bg-ink text-cream rounded-3xl p-8 md:p-10 text-center">
            <Sparkles
              size={28}
              className="text-gold mx-auto mb-4"
              fill="currentColor"
              strokeWidth={0}
            />
            <h3 className="font-serif text-2xl md:text-3xl mb-3">
              Jusqu'à <em className="text-gold not-italic">10 parrainages</em>{" "}
              par an
            </h3>
            <p className="text-cream/75 leading-relaxed">
              Chaque parrainage abouti = <strong className="text-gold">100 € de remise</strong>{" "}
              appliqués sur votre 2ᵉ mois de commission. Vous pouvez parrainer
              jusqu'à 10 propriétaires par année civile, soit{" "}
              <strong className="text-gold">jusqu'à 1 000 €</strong> de remise
              annuelle. Votre filleul reçoit lui aussi 100 € de remise sur son
              2ᵉ mois.
            </p>
          </div>

          {/* Conditions transparentes */}
          <div className="mt-10 max-w-4xl mx-auto bg-white/70 rounded-3xl p-7 border border-gold/20">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-3">
              Conditions transparentes
            </p>
            <ul className="space-y-2.5 text-sm text-ink/75">
              {[
                "Le filleul doit être propriétaire d'un bien locatif (résidence secondaire ou investissement) sur l'une des 30 communes que nous couvrons.",
                "Le bonus de 100 € est appliqué sur le 2ᵉ mois de commission, après la signature du contrat et la première mise en location effective du bien.",
                "Le mois où la remise s'applique correspond à la 2ᵉ facture conciergerie émise (parrain comme filleul).",
                "Cumul possible jusqu'à 10 parrainages aboutis par année civile, soit 1 000 € de remise maximum. Au-delà, contactez-nous pour un partenariat dédié.",
                "Aucun lien d'apporteur d'affaires : il s'agit d'un geste de remerciement, pas d'une activité commerciale.",
              ].map((c, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5"
                  data-testid={`parrainage-condition-${i}`}
                >
                  <CheckCircle2
                    size={16}
                    className="text-gold mt-0.5 shrink-0"
                    strokeWidth={2}
                  />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Form */}
      <section
        id="parrainage-form"
        className="py-20 bg-white"
        data-testid="parrainage-form-section"
      >
        <div className="container-tm">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <p className="overline mb-4">Formulaire de parrainage</p>
              <h2 className="font-serif text-3xl md:text-5xl text-ink leading-tight">
                Recommandez{" "}
                <em className="text-gold not-italic">votre filleul</em>
              </h2>
            </div>

            {status === "success" ? (
              <div
                className="bg-sage/15 border border-sage/40 rounded-3xl p-10 text-center"
                data-testid="parrainage-success"
              >
                <CheckCircle2
                  size={40}
                  className="text-sage mx-auto mb-4"
                  strokeWidth={1.6}
                />
                <h3 className="font-serif text-2xl text-ink mb-3">
                  Merci ! Votre parrainage est enregistré.
                </h3>
                <p className="text-ink/70 max-w-xl mx-auto">
                  Notre équipe contactera votre filleul sous 48h. Dès qu'il
                  signe avec nous, vos 100 € de remise seront appliqués
                  automatiquement sur votre 2ᵉ facture de commission.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 inline-flex items-center gap-2 text-gold hover:text-ink font-medium"
                  data-testid="parrainage-new"
                >
                  Parrainer une autre personne <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="space-y-8 bg-cream rounded-3xl p-7 md:p-10 border border-gold/20"
                data-testid="parrainage-form"
              >
                {/* Parrain */}
                <div>
                  <h3 className="font-serif text-xl text-ink mb-1">
                    👤 Vous (le parrain)
                  </h3>
                  <p className="text-ink/55 text-sm mb-5">
                    Pour vous identifier et créditer le mois offert.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Prénom *"
                      value={form.parrain_prenom}
                      onChange={update("parrain_prenom")}
                      required
                      testId="parrain-prenom"
                    />
                    <Input
                      label="Nom *"
                      value={form.parrain_nom}
                      onChange={update("parrain_nom")}
                      required
                      testId="parrain-nom"
                    />
                    <Input
                      label="Email *"
                      type="email"
                      value={form.parrain_email}
                      onChange={update("parrain_email")}
                      required
                      testId="parrain-email"
                    />
                    <Input
                      label="Téléphone"
                      type="tel"
                      value={form.parrain_telephone}
                      onChange={update("parrain_telephone")}
                      testId="parrain-telephone"
                    />
                  </div>
                </div>

                {/* Filleul */}
                <div className="border-t border-gold/20 pt-7">
                  <h3 className="font-serif text-xl text-ink mb-1">
                    🎁 Votre filleul
                  </h3>
                  <p className="text-ink/55 text-sm mb-5">
                    La personne que vous nous recommandez. Au moins l'email OU
                    le téléphone, pour qu'on puisse le contacter.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Prénom *"
                      value={form.filleul_prenom}
                      onChange={update("filleul_prenom")}
                      required
                      testId="filleul-prenom"
                    />
                    <Input
                      label="Nom *"
                      value={form.filleul_nom}
                      onChange={update("filleul_nom")}
                      required
                      testId="filleul-nom"
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={form.filleul_email}
                      onChange={update("filleul_email")}
                      testId="filleul-email"
                    />
                    <Input
                      label="Téléphone"
                      type="tel"
                      value={form.filleul_telephone}
                      onChange={update("filleul_telephone")}
                      testId="filleul-telephone"
                    />
                    <Input
                      label="Ville du bien (Saint-Malo, Cancale…)"
                      value={form.filleul_ville}
                      onChange={update("filleul_ville")}
                      testId="filleul-ville"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="border-t border-gold/20 pt-7">
                  <label className="block text-ink/70 text-xs uppercase tracking-[0.18em] font-semibold mb-2">
                    Un mot pour qu'on contextualise (facultatif)
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    placeholder="Ex : C'est ma voisine de la rue Saint-Vincent à Saint-Malo, elle galère avec une autre conciergerie depuis 6 mois…"
                    className="w-full bg-white px-4 py-3 rounded-xl border border-gold/30 focus:border-gold focus:outline-none text-ink text-sm placeholder:text-ink/30 transition-colors"
                    data-testid="parrainage-message"
                  />
                </div>

                {/* RGPD */}
                <label className="flex items-start gap-3 text-sm text-ink/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.rgpd_consent}
                    onChange={update("rgpd_consent")}
                    required
                    className="mt-1 shrink-0 accent-gold cursor-pointer"
                    data-testid="parrainage-rgpd"
                  />
                  <span>
                    J'accepte que Terre et Mer Conciergerie utilise les
                    coordonnées de mon filleul uniquement pour le contacter dans
                    le cadre de ce parrainage. Conformément au RGPD, ces
                    données ne seront jamais cédées à un tiers.
                  </span>
                </label>

                {errorMessage && (
                  <p
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-3"
                    data-testid="parrainage-error"
                  >
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold py-4 rounded-full hover:bg-ink hover:text-gold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="parrainage-submit"
                >
                  {status === "sending"
                    ? "Envoi en cours…"
                    : "Envoyer le parrainage"}
                  <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Input = ({ label, type = "text", value, onChange, required, testId }) => (
  <label className="block">
    <span className="block text-ink/70 text-[11px] uppercase tracking-[0.18em] font-semibold mb-1.5">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-white px-4 py-2.5 rounded-xl border border-gold/30 focus:border-gold focus:outline-none text-ink text-sm transition-colors"
      data-testid={`parrainage-${testId}`}
    />
  </label>
);

export default Parrainage;
